/**
 * Centralized ticket source color mapping utility
 * Provides consistent color styling for ticket sources across all components
 */

/**
 * Get Tailwind CSS classes for ticket source styling
 * @param {string} source - The ticket source value
 * @returns {string} Tailwind CSS classes for background, text, and border
 */
export const getTicketSourceTailwindClasses = (source) => {
  if (!source) return 'bg-gray-100 text-gray-800 border-gray-200';

  // Convert to uppercase for consistent mapping
  const normalizedSource = source.toString().toUpperCase();

  switch (normalizedSource) {
    case 'PDF':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'MANUAL_FLIGHT':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'MANUAL USER FLIGHT':
      return 'bg-indigo-100 text-indigo-800 border-indigo-200';
    case 'BOARDING PASS':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'EMAIL':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'IMAGE':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'MANUAL':
      return 'bg-pink-100 text-pink-800 border-pink-200';
    case 'FLYO':
      return 'bg-cyan-100 text-cyan-800 border-cyan-200';
    case 'INTERNAL':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'CALENDAR':
      return 'bg-teal-100 text-teal-800 border-teal-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

/**
 * Get ticket source styles for Radix UI components
 * @param {string} source - The ticket source value
 * @returns {object} Object containing bgColor, textColor, and borderColor classes
 */
export const getTicketSourceRadixStyles = (source) => {
  if (!source) {
    return {
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      borderColor: 'border-gray-200'
    };
  }

  // Convert to uppercase for consistent mapping
  const normalizedSource = source.toString().toUpperCase();

  switch (normalizedSource) {
    case 'PDF':
      return {
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200'
      };
    case 'MANUAL_FLIGHT':
      return {
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-200'
      };
    case 'MANUAL USER FLIGHT':
      return {
        bgColor: 'bg-indigo-100',
        textColor: 'text-indigo-800',
        borderColor: 'border-indigo-200'
      };
    case 'BOARDING PASS':
      return {
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      };
    case 'EMAIL':
      return {
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-200'
      };
    case 'IMAGE':
      return {
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-800',
        borderColor: 'border-purple-200'
      };
    case 'MANUAL':
      return {
        bgColor: 'bg-pink-100',
        textColor: 'text-pink-800',
        borderColor: 'border-pink-200'
      };
    case 'FLYO':
      return {
        bgColor: 'bg-cyan-100',
        textColor: 'text-cyan-800',
        borderColor: 'border-cyan-200'
      };
    case 'INTERNAL':
      return {
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-800',
        borderColor: 'border-orange-200'
      };
    case 'CALENDAR':
      return {
        bgColor: 'bg-teal-100',
        textColor: 'text-teal-800',
        borderColor: 'border-teal-200'
      };
    default:
      return {
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        borderColor: 'border-gray-200'
      };
  }
};

/**
 * Get ticket source indicator dot color classes for dropdown menus
 * @param {string} source - The ticket source value
 * @returns {string} Tailwind background color class for source indicator dot
 */
export const getTicketSourceIndicatorColor = (source) => {
  if (!source) return 'bg-gray-500';

  // Convert to uppercase for consistent mapping
  const normalizedSource = source.toString().toUpperCase();

  switch (normalizedSource) {
    case 'PDF':
      return 'bg-red-500';
    case 'MANUAL_FLIGHT':
      return 'bg-blue-500';
    case 'MANUAL USER FLIGHT':
      return 'bg-indigo-500';
    case 'BOARDING PASS':
      return 'bg-green-500';
    case 'EMAIL':
      return 'bg-yellow-500';
    case 'IMAGE':
      return 'bg-purple-500';
    case 'MANUAL':
      return 'bg-pink-500';
    case 'FLYO':
      return 'bg-cyan-500';
    case 'INTERNAL':
      return 'bg-orange-500';
    case 'CALENDAR':
      return 'bg-teal-500';
    default:
      return 'bg-gray-500';
  }
};

/**
 * Get ticket source text color classes
 * @param {string} source - The ticket source value
 * @returns {string} Tailwind text color class for source text
 */
export const getTicketSourceTextColor = (source) => {
  if (!source) return 'text-gray-600';

  // Convert to uppercase for consistent mapping
  const normalizedSource = source.toString().toUpperCase();

  switch (normalizedSource) {
    case 'PDF':
      return 'text-red-600';
    case 'MANUAL_FLIGHT':
      return 'text-blue-600';
    case 'MANUAL USER FLIGHT':
      return 'text-indigo-600';
    case 'BOARDING PASS':
      return 'text-green-600';
    case 'EMAIL':
      return 'text-yellow-600';
    case 'IMAGE':
      return 'text-purple-600';
    case 'MANUAL':
      return 'text-pink-600';
    case 'FLYO':
      return 'text-cyan-600';
    case 'INTERNAL':
      return 'text-orange-600';
    case 'CALENDAR':
      return 'text-teal-600';
    default:
      return 'text-gray-600';
  }
};

/**
 * Normalize ticket source value to uppercase
 * @param {string} source - The ticket source value
 * @returns {string} Normalized uppercase source value
 */
export const normalizeTicketSource = (source) => {
  if (!source) return 'N/A';
  return source.toString().toUpperCase();
};
